import { TbrData } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { CartItemViewData } from './../../items-list/business-logic/service/interfaces/cart-response';
import { UnitOfMeasureTypes, UomText } from '../enumerations/unit-of-measure-type';
import { IElectronicJournalRecord } from '../interfaces/states/IElectronicJournalRecord';
import { convertCurrency } from './currency-helper';
import { getValueAsNumber, truncateNumberWithPrecision } from './number-helper';
import { validateVolume } from './volume-uom-validate';
import { validateWeight } from './weight-uom-validate';
import { LOCAL_ISO_CURRENCY_CODE } from './localized-description-helper';
import { store } from '../redux/configure-store';
import { LineModifiersMethodCodes } from '../enumerations/line-modifiers-methods';

export interface ItemMeasureAttributes {
  baseUnit: number | string;
  unitOfMeasure: string;
  baseFinalLinePrice: string;
  atomicMsuUnit: string;
  atomicMsuUnitForPrice: string;
  atomicMsuPrice: string;
  atomicOverrideUnit: string;
  atomicOverridePrice: string;
  isPriceOverride: boolean;
  isMsu: boolean;
  showPreviousPrice: boolean;
}

export const saleQuantityCount = (item: IElectronicJournalRecord): string => {
  if (
    item.Quantity &&
    (validateWeight(item.UnitOfMeasureCode) || validateVolume(item.UnitOfMeasureCode))
  ) {
    return `${truncateNumberWithPrecision(item.Quantity ?? 1, false)} ${getUOM(item)}`;
  } else {
    return (item.Quantity ?? 0).toString();
  }
};

const getUOM = (item: IElectronicJournalRecord): string => {
  const selectedMeasureCode = item.UnitOfMeasureCode ?? '';
  return UomText[selectedMeasureCode];
};

export const itemMeasureAttributes = (
  item: CartItemViewData,
  tbrData: TbrData,
  isTBRActive?: boolean,
): ItemMeasureAttributes => {
  let baseUnit: number | string;
  const extendedAmount = isTBRActive
    ? Number(tbrData.ExtendedAmount)
    : item.extendedAmount ?? item.amount ?? 0;
  let baseFinalLinePrice: string = convertCurrency(extendedAmount, item.price?.currency, false);
  let atomicMsuUnitForPrice: string = '';
  let atomicMsuPrice: string = '';
  let atomicMsuUnit: string = '';
  let atomicOverridePrice: string = '';
  let atomicOverrideUnit = '';
  let unitOfMeasure = UomText[item?.lineItem?.quantity?.unitOfMeasure ?? UnitOfMeasureTypes.Each];

  const isMsu = (item.price?.quantity ?? 1) > 1;
  const isPriceChange =
    (item.price?.unitPrice &&
      item.regularPrice?.unitPrice &&
      item.price?.unitPrice !== item.regularPrice?.unitPrice &&
      item.price?.currency === item.regularPrice?.currency &&
      !item.lineItem?.quantity?.isPack) ||
    false;

  let quantity: number | undefined = item.lineItem?.quantity?.value;
  if (item.lineItem?.quantity?.unitOfMeasure) {
    quantity = item.lineItem.quantity.units || quantity;
  }
  if (isTBRActive) {
    quantity = tbrData.tbrAvailableQty;
  }

  const selectedItemQuantity = quantity ?? 0;

  const selectedMeasureCode = item?.lineItem?.quantity?.unitOfMeasure ?? UnitOfMeasureTypes.Each;

  let price: number = item.price?.unitPrice ?? 0;
  let overridePrice: number | undefined = item.originalExtendedAmount;

  if (
    item.lineItem?.priceModifier?.totalPriceDifference &&
    ['PriceOverride', 'EmbeddedPrice'].includes(item.lineItem.priceModifier.method ?? '')
  ) {
    const { unitPrice = 0 } = item.regularPrice || {};
    price = unitPrice;
    overridePrice = unitPrice;
  }

  if (price) {
    const { priceString, unitString } = convertUnitPriceCurrency(
      price,
      item.price?.currency,
      item.price?.quantity,
      unitOfMeasure,
    );
    atomicMsuPrice = priceString;
    atomicMsuUnit = unitString;
  }

  if (overridePrice) {
    const { priceString, unitString } = convertUnitPriceCurrency(
      overridePrice,
      item.price?.currency,
      item.price?.quantity,
      unitOfMeasure,
    );

    const { priceString: priceMsuString } = convertUnitPriceCurrency(
      item.price?.unitPrice ?? 0,
      item.price?.currency,
      item.price?.quantity,
      unitOfMeasure,
    );

    atomicOverridePrice = priceString;
    atomicOverrideUnit = unitString;
    atomicMsuPrice = priceMsuString;

    if (unitOfMeasure === UnitOfMeasureTypes.Each && isMsu) {
      atomicMsuUnitForPrice = `${item.price?.quantity}`;
      atomicOverrideUnit = `${atomicMsuUnitForPrice} ${unitOfMeasure}`;
    }

    // TODO: This should really be from a lookup table and not just have an s slapped on it
    if ((item.price?.quantity ?? 0) > 1) {
      atomicOverrideUnit += 's';
    }
  } else if (isPriceChange) {
    const { priceString, unitString } = convertUnitPriceCurrency(
      item.regularPrice?.unitPrice ?? 0,
      item.regularPrice?.currency,
      item.regularPrice?.quantity,
      unitOfMeasure,
    );
    atomicOverridePrice = priceString;
    atomicOverrideUnit = unitString;
  }

  if (validateWeight(item.lineItem?.quantity?.unitOfMeasure)) {
    atomicMsuUnit = `${unitOfMeasure}`;
    baseUnit = selectedItemQuantity.toFixed(
      store.getState().touchPoint.touchPointConfiguration.defaultWeightUnitOfMeasurePrecision,
    );
    baseFinalLinePrice = convertCurrency(extendedAmount ?? selectedItemQuantity * price);
  } else if (validateVolume(selectedMeasureCode)) {
    baseUnit = truncateNumberWithPrecision(selectedItemQuantity, false);
    atomicMsuUnit = `${unitOfMeasure}`;
    baseFinalLinePrice = convertCurrency(extendedAmount);
  } else {
    baseUnit = selectedItemQuantity;
    baseFinalLinePrice = convertCurrency(
      extendedAmount ?? (item.lineItem?.quantity?.value ?? 1) * price,
    );
    atomicMsuUnit = UomText.EA;
    if (getValueAsNumber(baseUnit) > 1) {
      unitOfMeasure += 's';
    }
  }

  if (isMsu && price) {
    atomicMsuUnitForPrice = `${item.price?.quantity}`;
    baseFinalLinePrice = convertCurrency(extendedAmount);
    if (getValueAsNumber(atomicMsuUnitForPrice) > 1 && atomicMsuUnit === UomText.EA.toString()) {
      atomicMsuUnit += 's';
    }
  }

  const isPriceOverride = !!item?.lineItem?.priceModifier?.amount || isPriceChange;

  const showPreviousPrice =
    isPriceOverride &&
    !!atomicOverridePrice &&
    !!item.lineItem?.priceModifier &&
    item.lineItem?.priceModifier?.method !== LineModifiersMethodCodes.ResetPrice &&
    item.lineItem?.priceModifier?.method !== LineModifiersMethodCodes.VerifyPrice &&
    item.lineItem?.priceModifier?.method !== LineModifiersMethodCodes.PriceVerify &&
    item.lineItem?.priceModifier?.method !== LineModifiersMethodCodes.RequiredPrice;

  return {
    baseUnit,
    unitOfMeasure,
    baseFinalLinePrice,
    atomicMsuUnit,
    atomicMsuUnitForPrice,
    atomicMsuPrice,
    atomicOverrideUnit,
    atomicOverridePrice,
    isPriceOverride,
    isMsu,
    showPreviousPrice,
  };
};

const convertUnitPriceCurrency = (
  price: number,
  currencyCode: string = LOCAL_ISO_CURRENCY_CODE,
  quantity: number = 1,
  unitOfMeasure: string = UnitOfMeasureTypes.Each,
): { priceString: string; unitString: string } => {
  const priceString = convertCurrency(price, currencyCode);
  const unitString = quantity > 1 ? `${quantity} ${unitOfMeasure}` : unitOfMeasure;
  return {
    priceString,
    unitString,
  };
};
